// /web/src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = import.meta.env?.VITE_API_URL || 'http://localhost:3001/api';

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref para evitar recrear funciones innecesariamente
  const isInitialized = useRef(false);

  // ✅ FUNCIONES ESTABLES - Sin dependencias problemáticas
  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    localStorage.removeItem('ehr-token');
    localStorage.removeItem('ehr-refresh-token');
    localStorage.removeItem('ehr-user');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const setAuth = useCallback((authData) => {
    const { usuario, token: newToken, refreshToken: newRefreshToken } = authData;

    setUser(usuario);
    setToken(newToken);
    setRefreshToken(newRefreshToken);
    
    localStorage.setItem('ehr-token', newToken);
    localStorage.setItem('ehr-refresh-token', newRefreshToken);
    localStorage.setItem('ehr-user', JSON.stringify(usuario));
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  }, []);

  // ✅ LOGIN - Estable, sin dependencias circulares
  const login = useCallback(async (email, password) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axios.post(`${API_URL}/usuarios/login`, {
        email,
        password
      });

      if (response.data.exito) {
        setAuth(response.data.datos);
        return response.data.datos;
      } else {
        throw new Error(response.data.mensaje || 'Error de autenticación');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.mensaje || 
                          error.message || 
                          'Error de conexión con el servidor';
      
      console.error('❌ Error de autenticación:', errorMessage);
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [setAuth]);

  // ✅ LOGOUT - Estable
  const logout = useCallback(() => {
    clearAuth();
    setError(null);
  }, [clearAuth]);

  // ✅ GET_PROFILE - Optimizado sin dependencias problemáticas
  const getProfile = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem('ehr-token');
      if (!currentToken && !axios.defaults.headers.common['Authorization']) {
        return null;
      }

      const response = await axios.get(`${API_URL}/usuarios/perfil`);
      
      if (response.data.exito) {
        const userProfile = response.data.datos;
        setUser(userProfile);
        localStorage.setItem('ehr-user', JSON.stringify(userProfile));
        return userProfile;
      }
    } catch (error) {
      console.error('❌ Error al obtener perfil:', error.response?.data?.mensaje || error.message);
      if (error.response?.status === 401) {
        clearAuth();
      }
    }
    return null;
  }, [clearAuth]);

  // ✅ VALORES COMPUTADOS - Memoizados correctamente
  const computedValues = useMemo(() => {
    const isAuthenticated = !!(token && user);
    const isAdmin = user?.['100']?.a === 'admin' || user?.username === 'admin';
    const userEmail = user?.['100']?.b || user?.email || '';
    const userName = user?.['200']?.a || user?.['100']?.a || user?.username || '';
    const userFullName = user?.['200'] ? `${user['200'].a || ''} ${user['200'].b || ''}`.trim() : userName;

    return {
      isAuthenticated,
      isAdmin,
      userEmail,
      userName,
      userFullName
    };
  }, [user, token]);

  // ✅ INICIALIZACIÓN - Solo una vez
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const storedToken = localStorage.getItem('ehr-token');
    const storedRefreshToken = localStorage.getItem('ehr-refresh-token');
    const storedUser = localStorage.getItem('ehr-user');

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setToken(storedToken);
        setRefreshToken(storedRefreshToken);
        
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        getProfile().catch(() => clearAuth());
        
      } catch (error) {
        //console.error('❌ Error al restaurar sesión:', error);
        clearAuth();
      }
    } else {
      if (import.meta.env.DEV) {
        //console.log('ℹ️ No hay sesión previa para restaurar');
      }
    }
    
    setIsLoading(false);
  }, [getProfile, clearAuth]);

  // ✅ AXIOS INTERCEPTOR - Solo una vez
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          if (import.meta.env.DEV) {
            console.log('🔑 Token expirado detectado, cerrando sesión...');
          }
          clearAuth();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [clearAuth]);

  // ✅ VALUE FINAL - Memoizado con dependencias mínimas
  const contextValue = useMemo(() => ({
    // Estados básicos
    user,
    token,
    refreshToken,
    isLoading,
    error,
    
    // Valores computados
    ...computedValues,
    
    // Funciones estables
    login,
    logout,
    getProfile,
    clearError
  }), [
    user,
    token,
    refreshToken,
    isLoading,
    error,
    computedValues,
    login,
    logout,
    getProfile,
    clearError
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
export default AuthContext;